/*
 * Setlist Shop storefront analytics
 *
 * Pushes GA4-aligned ecommerce events to window.SLDataLayer for GTM (GTM-562FK87T) to pick up.
 * Storefront-only — checkout tracking lives in a separate Shopify Custom Pixel.
 *
 * Events emitted (all use the _dl suffix during QA / vendor-GA4 coexistence):
 *   view_item_list_dl            - on homepage load, listing all featured products
 *   select_variant_dl            - when the variant picker resolves to a new variant id
 *   add_to_cart_dl               - initial ATC (via product-form pub/sub)
 *   remove_from_cart_dl          - line removed / quantity → 0
 *   cart_quantity_increased_dl   - line quantity went up (still > 0)
 *   cart_quantity_decreased_dl   - line quantity went down (still > 0)
 *   view_cart_dl                 - cart drawer opens
 */
(function () {
  'use strict';

  window.SLDataLayer = window.SLDataLayer || [];
  var DL = window.SLDataLayer;

  var DEFAULT_CURRENCY =
    (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';

  var blobsBySection = {};
  var lastVariantBySection = {};
  var previousCart = null;
  var cartFetchToken = 0;

  function toMajor(cents) {
    if (cents === undefined || cents === null) return undefined;
    var n = Number(cents);
    if (isNaN(n)) return undefined;
    return Math.round(n) / 100;
  }

  function toStr(v) {
    return v === undefined || v === null ? undefined : String(v);
  }

  function cleanObject(obj) {
    var out = {};
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var val = obj[k];
      if (val !== undefined && val !== null && val !== '') out[k] = val;
    }
    return out;
  }

  function mapCartLine(line, qtyOverride) {
    var unitPrice = line.final_price != null ? line.final_price : line.price;
    return cleanObject({
      item_id: toStr(line.variant_id),
      item_name: line.product_title || line.title,
      item_brand: line.vendor,
      item_category: line.product_type,
      item_variant: line.variant_title,
      price: toMajor(unitPrice),
      quantity: Number(qtyOverride != null ? qtyOverride : line.quantity)
    });
  }

  function mapBlobItem(blob) {
    return cleanObject({
      item_id: toStr(blob.item_id),
      item_name: blob.item_name,
      item_brand: blob.item_brand,
      item_category: blob.item_category,
      item_variant: blob.item_variant,
      price: Number(blob.price),
      quantity: 1
    });
  }

  function readProductBlobs() {
    var nodes = document.querySelectorAll('script.sl-product-json');
    var blobs = [];
    for (var i = 0; i < nodes.length; i++) {
      try {
        var data = JSON.parse(nodes[i].textContent);
        blobs.push(data);
        var sid = nodes[i].getAttribute('data-section');
        if (sid) blobsBySection[sid] = data;
      } catch (e) {
        // skip malformed blob
      }
    }
    return blobs;
  }

  function fireInitialViewEvents(blobs) {
    if (!blobs.length) return;

    var pdpBlobs = [];
    var listBlobs = [];
    for (var i = 0; i < blobs.length; i++) {
      if (blobs[i].surface === 'pdp') pdpBlobs.push(blobs[i]);
      else listBlobs.push(blobs[i]);
    }

    for (var p = 0; p < pdpBlobs.length; p++) {
      var b = pdpBlobs[p];
      var item = mapBlobItem(b);
      DL.push({
        event: 'view_item_dl',
        ecommerce: cleanObject({
          currency: b.currency || DEFAULT_CURRENCY,
          value: item.price,
          items: [item]
        })
      });
    }

    if (listBlobs.length) {
      var first = listBlobs[0];
      DL.push({
        event: 'view_item_list_dl',
        ecommerce: cleanObject({
          item_list_name: first.item_list_name || 'Homepage',
          item_list_id: first.item_list_id || 'homepage',
          currency: first.currency || DEFAULT_CURRENCY,
          items: listBlobs.map(mapBlobItem)
        })
      });
    }
  }

  function fireViewCartOnCartPage() {
    // <cart-items> is only present on the standalone /cart page; the drawer
    // renders <cart-drawer-items>. Use that as the cart-page signal.
    if (!document.querySelector('cart-items')) return;
    refreshPreviousCart().then(function (cart) {
      var c = cart || previousCart || { items: [], currency: DEFAULT_CURRENCY };
      var items = (c.items || []).map(function (l) { return mapCartLine(l); });
      DL.push({
        event: 'view_cart_dl',
        ecommerce: cleanObject({
          currency: c.currency || DEFAULT_CURRENCY,
          value: toMajor(c.total_price),
          items: items
        })
      });
    });
  }

  function setupVariantTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    for (var sid in blobsBySection) {
      if (!Object.prototype.hasOwnProperty.call(blobsBySection, sid)) continue;
      lastVariantBySection[sid] = {
        item_id: String(blobsBySection[sid].item_id),
        item_variant: blobsBySection[sid].item_variant
      };
    }

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var data = event && event.data;
      if (!data || !data.variant) return;
      var sectionId = data.sectionId;
      var variant = data.variant;
      var newId = String(variant.id);
      var prev = lastVariantBySection[sectionId];

      if (prev && prev.item_id === newId) return;

      var blob = blobsBySection[sectionId] || {};
      var item = cleanObject({
        item_id: newId,
        item_name: variant.product_title || blob.item_name,
        item_brand: blob.item_brand,
        item_category: blob.item_category,
        item_variant: variant.title,
        price: toMajor(variant.price),
        quantity: 1
      });

      DL.push(cleanObject({
        event: 'select_variant_dl',
        ecommerce: cleanObject({
          currency: blob.currency || DEFAULT_CURRENCY,
          items: [item]
        }),
        previous_item_id: prev ? prev.item_id : undefined,
        previous_item_variant: prev ? prev.item_variant : undefined
      }));

      lastVariantBySection[sectionId] = { item_id: newId, item_variant: variant.title };
    });
  }

  function findLineByVariant(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  function indexCartByKey(cart) {
    var byKey = {};
    if (!cart || !cart.items) return byKey;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      byKey[line.key || line.variant_id + ':' + i] = line;
    }
    return byKey;
  }

  function pushCartEvent(name, line, qty, currency) {
    var item = mapCartLine(line, qty);
    var value = (item.price || 0) * (item.quantity || 0);
    DL.push({
      event: name,
      ecommerce: cleanObject({
        currency: currency || DEFAULT_CURRENCY,
        value: Number(value.toFixed(2)),
        items: [item]
      })
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  // Token-gated cart refresh: only the most recently started fetch may overwrite
  // previousCart. Older fetches that resolve late are discarded, preventing a
  // stale snapshot from clobbering fresher in-memory state.
  function refreshPreviousCart() {
    var token = ++cartFetchToken;
    return fetchCart().then(function (cart) {
      if (cart && token === cartFetchToken) previousCart = cart;
      return cart;
    });
  }

  // Synchronously update previousCart to reflect a just-added line item.
  // The /cart/add.js response carries the line's *cumulative* quantity after
  // the add, so we set (not increment) the cached line's quantity.
  function applyAddedLineToPreviousCart(line) {
    if (!previousCart) previousCart = { items: [], currency: DEFAULT_CURRENCY };
    if (!previousCart.items) previousCart.items = [];
    for (var i = 0; i < previousCart.items.length; i++) {
      if (String(previousCart.items[i].variant_id) === String(line.variant_id)) {
        previousCart.items[i].quantity = Number(line.quantity);
        return;
      }
    }
    previousCart.items.push({
      key: line.key,
      variant_id: line.variant_id,
      product_id: line.product_id,
      product_title: line.product_title,
      variant_title: line.variant_title,
      vendor: line.vendor,
      product_type: line.product_type,
      price: line.price,
      final_price: line.final_price,
      quantity: Number(line.quantity)
    });
  }

  function diffAndPushChanges(cartData, source, productVariantId) {
    var hasItems = cartData && Array.isArray(cartData.items);
    var currency =
      (cartData && cartData.currency) ||
      (previousCart && previousCart.currency) ||
      DEFAULT_CURRENCY;

    if (source === 'product-form') {
      // cartData from product-form is the just-added line item (not a full cart).
      // line.quantity is the line's cumulative quantity after the add, so the
      // delta = lineQty - prevQty. Floor at 1 in case lineQty is reported as
      // the delta on some platform variants.
      var line = hasItems ? findLineByVariant(cartData, productVariantId) : cartData;
      if (line) {
        var prevLine = previousCart ? findLineByVariant(previousCart, productVariantId) : null;
        var prevQty = prevLine ? Number(prevLine.quantity) : 0;
        var lineQty = Number(line.quantity) || 1;
        var delta = Math.max(1, lineQty - prevQty);
        pushCartEvent('add_to_cart_dl', line, delta, currency);
        applyAddedLineToPreviousCart(line);
      }
      refreshPreviousCart();
      return;
    }

    if (source !== 'cart-items' || !hasItems) return;

    var oldByKey = indexCartByKey(previousCart);
    var newByKey = indexCartByKey(cartData);
    var k;

    for (k in oldByKey) {
      if (!Object.prototype.hasOwnProperty.call(oldByKey, k)) continue;
      var oldLine = oldByKey[k];
      var newLine = newByKey[k];
      if (!newLine) {
        pushCartEvent('remove_from_cart_dl', oldLine, Number(oldLine.quantity), currency);
        continue;
      }
      var oldQty = Number(oldLine.quantity);
      var newQty = Number(newLine.quantity);
      if (newQty < oldQty) {
        pushCartEvent('cart_quantity_decreased_dl', oldLine, oldQty - newQty, currency);
      } else if (newQty > oldQty) {
        pushCartEvent('cart_quantity_increased_dl', newLine, newQty - oldQty, currency);
      }
    }
    for (k in newByKey) {
      if (!Object.prototype.hasOwnProperty.call(newByKey, k)) continue;
      if (!oldByKey[k]) {
        pushCartEvent('cart_quantity_increased_dl', newByKey[k], Number(newByKey[k].quantity), currency);
      }
    }

    previousCart = cartData;
  }

  function setupCartTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    refreshPreviousCart().then(function (cart) {
      if (!cart) previousCart = { items: [], currency: DEFAULT_CURRENCY };
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || !event.cartData) return;
      diffAndPushChanges(event.cartData, event.source, event.productVariantId);
    });
  }

  function setupCartDrawerTracking() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var wasActive = drawer.classList.contains('active');
    var observer = new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) {
        refreshPreviousCart().then(function (cart) {
          var c = cart || previousCart || { items: [], currency: DEFAULT_CURRENCY };
          var items = (c.items || []).map(function (l) { return mapCartLine(l); });
          DL.push({
            event: 'view_cart_dl',
            ecommerce: cleanObject({
              currency: c.currency || DEFAULT_CURRENCY,
              value: toMajor(c.total_price),
              items: items
            })
          });
        });
      }
      wasActive = isActive;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    var blobs = readProductBlobs();
    fireInitialViewEvents(blobs);
    fireViewCartOnCartPage();
    setupVariantTracking();
    setupCartTracking();
    setupCartDrawerTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
